import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Database, CheckCircle2, FileText, Users, Tag, MapPin, Mail, Clock, Copy, Check } from 'lucide-react';
import { campaignsApi, CampaignRecord } from '../services/creatorApi';
import { toast } from 'sonner';
export function CampaignDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<CampaignRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [copiedItem, setCopiedItem] = useState<string | null>(null);
  useEffect(() => {
    const loadCampaign = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const data = await campaignsApi.get(id);
        setCampaign(data);
      } catch (err) {
        console.error('Failed to load campaign:', err);
        toast.error('Failed to load campaign');
      } finally {
        setLoading(false);
      }
    };
    loadCampaign();
  }, [id]);
  const handleCopy = (text: string, itemId: string) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(itemId);
    setTimeout(() => setCopiedItem(null), 2000);
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Sent':
        return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-50 text-green-700 border border-green-200" data-id="element-1785">
            <CheckCircle2 className="h-4 w-4" data-id="element-1786" />
            Sent
          </span>;
      case 'Exported':
        return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-purple-50 text-purple-700 border border-purple-200" data-id="element-1787">
            <Database className="h-4 w-4" data-id="element-1788" />
            Exported
          </span>;
      default:
        return <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 border border-gray-200" data-id="element-1789">
            <FileText className="h-4 w-4" data-id="element-1790" />
            Draft
          </span>;
    }
  };
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-1791">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-1792" />
      </div>;
  }
  if (!campaign) {
    return <div className="max-w-3xl mx-auto text-center py-16" data-id="element-1793">
        <FileText className="mx-auto h-12 w-12 text-gray-400" data-id="element-1794" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900" data-id="element-1795">
          Campaign not found
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-1796">
          This campaign may have been deleted.
        </p>
        <Link to="/campaigns" className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-1797">
          <ArrowLeft className="h-4 w-4" data-id="element-1798" />
          Back to Campaigns
        </Link>
      </div>;
  }
  return <div className="max-w-3xl mx-auto space-y-6 pb-12" data-id="element-1799">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1800">
        <div className="flex items-center gap-4" data-id="element-1801">
          <button onClick={() => navigate('/campaigns')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md" data-id="element-1802">
            <ArrowLeft className="h-5 w-5" data-id="element-1803" />
          </button>
          <div data-id="element-1804">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-1805">
              {campaign.Campaign_Name || 'Untitled Campaign'}
            </h1>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-1806">
              ID:{' '}
              <code className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-1807">
                {campaign.Campaign_ID}
              </code>
            </p>
          </div>
        </div>
        {getStatusBadge(campaign.Status)}
      </div>

      {/* Campaign Details */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm divide-y divide-gray-200" data-id="element-1808">
        <div className="p-6" data-id="element-1809">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-1810">
            Email Details
          </h2>
          <div className="grid grid-cols-2 gap-y-4 gap-x-8" data-id="element-1811">
            <div data-id="element-1812">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-1813">
                Subject
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-1814">
                {campaign.Email_Subject || '-'}
              </span>
            </div>
            <div data-id="element-1815">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-1816">
                Preheader
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-1817">
                {campaign.Preheader_Text || '-'}
              </span>
            </div>
            <div data-id="element-1818">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-1819">
                From Name
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-1820">
                {campaign.From_Name || '-'}
              </span>
            </div>
            <div data-id="element-1821">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-1822">
                From Email
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-1823">
                {campaign.From_Email || '-'}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6" data-id="element-1824">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-1825">
            Export Details
          </h2>
          <div className="grid grid-cols-2 gap-y-4 gap-x-8" data-id="element-1826">
            <div data-id="element-1827">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-1828">
                Recipients
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block font-medium" data-id="element-1829">
                {campaign.Recipient_Count ? campaign.Recipient_Count.toLocaleString() : '-'}
              </span>
            </div>
            <div data-id="element-1830">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-1831">
                Exported At
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-1832">
                {formatDate(campaign.Exported_At)}
              </span>
            </div>
            {campaign.Bucket_ID && <div className="col-span-2" data-id="element-1833">
                <span className="text-xs font-medium text-gray-500 block mb-1" data-id="element-1834">
                  Bucket ID
                </span>
                <div className="flex items-center gap-2" data-id="element-1835">
                  <code className="text-sm font-mono bg-purple-50 text-purple-700 px-3 py-1.5 rounded border border-purple-100" data-id="element-1836">
                    {campaign.Bucket_ID}
                  </code>
                  <button onClick={() => handleCopy(campaign.Bucket_ID, 'bucket')} className="text-gray-400 hover:text-gray-600 p-1.5 hover:bg-gray-100 rounded" title="Copy Bucket ID" data-id="element-1837">
                    {copiedItem === 'bucket' ? <Check className="h-4 w-4 text-green-500" data-id="element-1838" /> : <Copy className="h-4 w-4" data-id="element-1839" />}
                  </button>
                </div>
              </div>}
            {campaign.Bucket_Expires_At && <div data-id="element-1840">
                <span className="text-xs font-medium text-gray-500 block" data-id="element-1841">
                  Bucket Expires
                </span>
                <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-1842">
                  {campaign.Bucket_Expires_At}
                </span>
              </div>}
          </div>
        </div>

        <div className="p-6" data-id="element-1843">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-1844">
            Scope
          </h2>
          <div className="grid grid-cols-2 gap-y-4 gap-x-8" data-id="element-1845">
            <div data-id="element-1846">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-1847">
                Team
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-1848">
                {campaign.Team || '-'}
              </span>
            </div>
            <div data-id="element-1849">
              <span className="text-xs font-medium text-gray-500 block" data-id="element-1850">
                Region
              </span>
              <span className="text-sm text-gray-900 mt-0.5 block" data-id="element-1851">
                {campaign.Region || 'All'}
              </span>
            </div>
          </div>
        </div>

        {/* Targeting */}
        {(campaign.Campaign_Target_Regions?.length || campaign.Campaign_Target_Mailing_Lists?.length || campaign.Campaign_Target_Tags?.length) && <div className="p-6" data-id="element-1852">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-1853">
              Targeting
            </h2>
            <div className="space-y-4" data-id="element-1854">
              {campaign.Campaign_Target_Regions && campaign.Campaign_Target_Regions.length > 0 && <div data-id="element-1855">
                    <span className="text-xs font-medium text-gray-500 flex items-center gap-1 mb-2" data-id="element-1856">
                      <MapPin className="h-3 w-3" data-id="element-1857" /> Regions
                    </span>
                    <div className="flex flex-wrap gap-2" data-id="element-1858">
                      {campaign.Campaign_Target_Regions.map((r, i) => <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-1859">
                          {r.Region_Name}
                        </span>)}
                    </div>
                  </div>}
              {campaign.Campaign_Target_Mailing_Lists && campaign.Campaign_Target_Mailing_Lists.length > 0 && <div data-id="element-1860">
                    <span className="text-xs font-medium text-gray-500 flex items-center gap-1 mb-2" data-id="element-1861">
                      <Mail className="h-3 w-3" data-id="element-1862" /> Mailing Lists
                    </span>
                    <div className="flex flex-wrap gap-2" data-id="element-1863">
                      {campaign.Campaign_Target_Mailing_Lists.map((l, i) => <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-1864">
                          {l.Mailing_List_Name || l.Mailing_List_ID}
                        </span>)}
                    </div>
                  </div>}
              {campaign.Campaign_Target_Tags && campaign.Campaign_Target_Tags.length > 0 && <div data-id="element-1865">
                    <span className="text-xs font-medium text-gray-500 flex items-center gap-1 mb-2" data-id="element-1866">
                      <Tag className="h-3 w-3" data-id="element-1867" /> Tags
                    </span>
                    <div className="flex flex-wrap gap-2" data-id="element-1868">
                      {campaign.Campaign_Target_Tags.map((t, i) => <span key={i} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-1869">
                          {t.Tag_Name || t.Tag_ID}
                        </span>)}
                    </div>
                  </div>}
            </div>
          </div>}

        {/* Content Preview */}
        {(campaign.HTML_Content || campaign.Text_Content) && <div className="p-6" data-id="element-1870">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4" data-id="element-1871">
              Content
            </h2>
            {campaign.Text_Content && <div data-id="element-1872">
                <span className="text-xs font-medium text-gray-500 block mb-2" data-id="element-1873">
                  Plain Text
                </span>
                <pre className="text-sm text-gray-700 bg-gray-50 rounded-lg p-4 border border-gray-200 whitespace-pre-wrap font-mono max-h-48 overflow-auto" data-id="element-1874">
                  {campaign.Text_Content}
                </pre>
              </div>}
          </div>}
      </div>

      {/* Zoho Campaigns Filter Helper */}
      {campaign.Bucket_ID && <div className="bg-blue-50 border border-blue-200 rounded-lg p-5" data-id="element-1875">
          <h3 className="text-sm font-semibold text-blue-900 mb-2" data-id="element-1876">
            Zoho Campaigns Segment Filter
          </h3>
          <div className="flex items-center gap-2" data-id="element-1877">
            <code className="flex-1 text-sm font-mono bg-white text-blue-900 px-3 py-2 rounded border border-blue-200" data-id="element-1878">
              Bucket_ID equals {campaign.Bucket_ID}
            </code>
            <button onClick={() => handleCopy(`Bucket_ID equals ${campaign.Bucket_ID}`, 'filter')} className="px-3 py-2 text-sm font-medium bg-blue-100 text-blue-700 rounded hover:bg-blue-200" data-id="element-1879">
              {copiedItem === 'filter' ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>}
    </div>;
}